// Shared configuration for all supported translation pairs.
// Each pair drives its own SEO-optimized landing page + internal links.

export const LANG_META = {
  english: { name: "English", native: "English", script: "Latin", code: "en" },
  hindi: { name: "Hindi", native: "हिन्दी", script: "Devanagari", code: "hi" },
  tamil: { name: "Tamil", native: "தமிழ்", script: "Tamil", code: "ta" },
  marathi: { name: "Marathi", native: "मराठी", script: "Devanagari", code: "mr" },
  malayalam: { name: "Malayalam", native: "മലയാളം", script: "Malayalam", code: "ml" },
  telugu: { name: "Telugu", native: "తెలుగు", script: "Telugu", code: "te" },
  gujarati: { name: "Gujarati", native: "ગુજરાતી", script: "Gujarati", code: "gu" },
  spanish: { name: "Spanish", native: "Español", script: "Latin", code: "es" },
  french: { name: "French", native: "Français", script: "Latin", code: "fr" },
  mandarin: { name: "Mandarin", native: "中文", script: "Chinese (Simplified)", code: "zh-CN" },
  german: { name: "German", native: "Deutsch", script: "Latin", code: "de" },
  mizo: { name: "Mizo", native: "Mizo ṭawng", script: "Latin", code: "lus" },
  urdu: { name: "Urdu", native: "اردو", script: "Nastaliq (Arabic)", code: "ur" },
  tagalog: { name: "Tagalog", native: "Tagalog", script: "Latin", code: "tl" },
  afrikaans: { name: "Afrikaans", native: "Afrikaans", script: "Latin", code: "af" },
  filipino: { name: "Filipino", native: "Filipino", script: "Latin", code: "fil" },
  zulu: { name: "Zulu", native: "isiZulu", script: "Latin", code: "zu" },
  maori: { name: "Maori", native: "Te Reo Māori", script: "Latin", code: "mi" },
  korean: { name: "Korean", native: "한국어", script: "Hangul", code: "ko" },
  arabic: { name: "Arabic", native: "العربية", script: "Arabic", code: "ar" },
  georgian: { name: "Georgian", native: "ქართული", script: "Georgian (Mkhedruli)", code: "ka" },
  albanian: { name: "Albanian", native: "Shqip", script: "Latin", code: "sq" },
};

export const PAIRS = [
  {
    slug: "hindi-to-english",
    source: "hindi",
    target: "english",
    keyword: "Hindi to English Translation",
    title: "Hindi to English Translation | Accurate, Free & Instant",
    description:
      "Translate Hindi to English online — fast, accurate, and free. Get precise Hindi to English translations for sentences, paragraphs and documents.",
    intro:
      "Convert Hindi text into natural, fluent English in seconds. Ideal for students, business emails, WhatsApp chats, essays and documentation.",
  },
  {
    slug: "english-to-hindi",
    source: "english",
    target: "hindi",
    keyword: "English to Hindi Translation",
    title: "English to Hindi Translation | Accurate, Free & Instant",
    description:
      "Translate English to Hindi online — instant, free and grammatically correct. Perfect for emails, homework, blogs and everyday chats.",
    intro:
      "Type any English sentence and get a natural Hindi (हिन्दी) translation instantly. Optimised for tone, grammar and cultural nuance.",
  },
  {
    slug: "english-to-tamil",
    source: "english",
    target: "tamil",
    keyword: "English to Tamil Translation",
    title: "English to Tamil Translation | Accurate & Free Online",
    description:
      "Translate English to Tamil (தமிழ்) online — free, fast and precise. Get fluent Tamil translations for messages, essays and documents.",
    intro:
      "Turn English words and paragraphs into elegant Tamil text. Trusted by students, professionals and creators across Tamil Nadu and abroad.",
  },
  {
    slug: "english-to-marathi",
    source: "english",
    target: "marathi",
    keyword: "English to Marathi Translation",
    title: "English to Marathi Translation | Fast & Free",
    description:
      "Translate English to Marathi (मराठी) with high accuracy. Free, instant and reliable Marathi translations for any context.",
    intro:
      "Instantly translate English into fluent Marathi. Great for Maharashtra-based businesses, students and everyday communication.",
  },
  {
    slug: "english-to-malayalam",
    source: "english",
    target: "malayalam",
    keyword: "English to Malayalam Translation",
    title: "English to Malayalam Translation | Free & Accurate",
    description:
      "Translate English to Malayalam (മലയാളം) online. Free, instant and context-aware Malayalam translations by AI.",
    intro:
      "Get authentic Malayalam translations from English text — perfect for Kerala-based businesses, students and creators.",
  },
  {
    slug: "english-to-telugu",
    source: "english",
    target: "telugu",
    keyword: "English to Telugu Translation",
    title: "English to Telugu Translation | Instant & Free",
    description:
      "Translate English to Telugu (తెలుగు) instantly and for free. Accurate translations powered by AI with cultural nuance.",
    intro:
      "Convert English text into readable, natural Telugu in one click. Ideal for Andhra Pradesh & Telangana audiences.",
  },
  {
    slug: "english-to-gujarati",
    source: "english",
    target: "gujarati",
    keyword: "English to Gujarati Translation",
    title: "English to Gujarati Translation | Free & Accurate",
    description:
      "Translate English to Gujarati (ગુજરાતી) with fluency and precision. Free, instant AI-powered Gujarati translations.",
    intro:
      "Convert English into flowing, native-sounding Gujarati text — perfect for business, family messages and content.",
  },
  {
    slug: "english-to-spanish",
    source: "english",
    target: "spanish",
    keyword: "English to Spanish Translation",
    title: "English to Spanish Translation | Free, Fast & Accurate",
    description:
      "Translate English to Spanish (Español) online — free, instant and grammatically correct. Perfect for travel, work and study.",
    intro:
      "Get natural, native-sounding Spanish translations of any English text — great for travel, business emails and language learners.",
  },
  {
    slug: "french-to-english",
    source: "french",
    target: "english",
    keyword: "French to English Translation",
    title: "French to English Translation | Instant & Free",
    description:
      "Translate French (Français) to English online — free, fast and accurate. Ideal for documents, articles and everyday phrases.",
    intro:
      "Instantly convert French text into clear, fluent English. Trusted by students, travellers and professionals.",
  },
  {
    slug: "english-to-mandarin",
    source: "english",
    target: "mandarin",
    keyword: "English to Mandarin Translation",
    title: "English to Mandarin Chinese Translation | Free & Accurate",
    description:
      "Translate English to Mandarin Chinese (中文) online — free, instant and precise. Simplified script output for business and study.",
    intro:
      "Convert English words and paragraphs into fluent Simplified Chinese (中文). Perfect for business, travel and cross-cultural communication.",
  },
  {
    slug: "german-to-english",
    source: "german",
    target: "english",
    keyword: "German to English Translation",
    title: "German to English Translation | Instant & Free",
    description:
      "Translate German (Deutsch) to English online — free, fast and accurate. Reliable for documents, emails and everyday phrases.",
    intro:
      "Turn any German text into natural English in seconds. Ideal for students, expats and international business.",
  },
  {
    slug: "english-to-german",
    source: "english",
    target: "german",
    keyword: "English to German Translation",
    title: "English to German Translation | Free & Precise",
    description:
      "Translate English to German (Deutsch) instantly and for free. Accurate translations with proper grammar and case handling.",
    intro:
      "Convert English into fluent, grammatically correct German — great for work, study and travel across Germany, Austria and Switzerland.",
  },
  {
    slug: "english-to-mizo",
    source: "english",
    target: "mizo",
    keyword: "English to Mizo Translation",
    title: "English to Mizo Translation | Free & Accurate",
    description:
      "Translate English to Mizo (Mizo ṭawng) online — free, instant and accurate. Get natural Mizo translations for messages, essays and documents.",
    intro:
      "Convert English text into natural Mizo instantly. Perfect for students, content creators and anyone communicating in Mizoram and the Northeast.",
  },
  {
    slug: "english-to-urdu",
    source: "english",
    target: "urdu",
    keyword: "English to Urdu Translation",
    title: "English to Urdu Translation | Free, Fast & Accurate",
    description:
      "Translate English to Urdu (اردو) online — free, instant and accurate. Get fluent Urdu translations for messages, documents and everyday use.",
    intro:
      "Convert English text into elegant, natural Urdu instantly. Perfect for students, writers and professionals who need accurate Urdu translations.",
  },
  {
    slug: "english-to-tagalog",
    source: "english",
    target: "tagalog",
    keyword: "English to Tagalog Translation",
    title: "English to Tagalog Translation | Free & Instant",
    description:
      "Translate English to Tagalog online — free, fast and accurate. Get natural Tagalog translations for conversations, essays and documents.",
    intro:
      "Turn English words and paragraphs into fluent Tagalog text. Trusted by students, travellers and Filipino communities worldwide.",
  },
  {
    slug: "english-to-afrikaans",
    source: "english",
    target: "afrikaans",
    keyword: "English to Afrikaans Translation",
    title: "English to Afrikaans Translation | Free & Accurate",
    description:
      "Translate English to Afrikaans online — free, instant and precise. Get natural Afrikaans translations for messages, essays and documents.",
    intro:
      "Convert English text into fluent Afrikaans instantly. Ideal for South African businesses, students and everyday communication.",
  },
  {
    slug: "filipino-to-english",
    source: "filipino",
    target: "english",
    keyword: "Filipino to English Translation",
    title: "Filipino to English Translation | Instant & Free",
    description:
      "Translate Filipino to English online — free, fast and accurate. Ideal for documents, messages and everyday phrases.",
    intro:
      "Instantly convert Filipino text into clear, fluent English. Trusted by students, professionals and Filipinos communicating globally.",
  },
  {
    slug: "english-to-zulu",
    source: "english",
    target: "zulu",
    keyword: "English to Zulu Translation",
    title: "English to Zulu Translation | Free, Fast & Accurate",
    description:
      "Translate English to Zulu (isiZulu) online — free, instant and accurate. Get natural Zulu translations for messages, essays and documents.",
    intro:
      "Convert English text into fluent isiZulu instantly. Perfect for South African communication, students and content creators.",
  },
  {
    slug: "english-to-maori",
    source: "english",
    target: "maori",
    keyword: "English to Maori Translation",
    title: "English to Maori Translation | Free & Accurate",
    description:
      "Translate English to Maori (Te Reo Māori) online — free, instant and precise. Get authentic Maori translations for any context.",
    intro:
      "Turn English words into beautiful Te Reo Māori. Ideal for learners, educators and anyone celebrating New Zealand's indigenous language.",
  },
  {
    slug: "english-to-korean",
    source: "english",
    target: "korean",
    keyword: "English to Korean Translation",
    title: "English to Korean Translation | Free, Fast & Accurate",
    description:
      "Translate English to Korean (한국어) online — free, instant and natural. Get fluent Korean translations for messages, documents and everyday use.",
    intro:
      "Convert English text into natural Korean instantly. Perfect for K-culture fans, business professionals and students learning Hangul.",
  },
  {
    slug: "arabic-to-english",
    source: "arabic",
    target: "english",
    keyword: "Arabic to English Translation",
    title: "Arabic to English Translation | Instant & Free",
    description:
      "Translate Arabic (العربية) to English online — free, fast and accurate. Ideal for documents, messages and everyday phrases.",
    intro:
      "Instantly convert Arabic text into clear, fluent English. Trusted by students, travellers and professionals worldwide.",
  },
  {
    slug: "english-to-georgian",
    source: "english",
    target: "georgian",
    keyword: "English to Georgian Translation",
    title: "English to Georgian Translation | Free & Accurate",
    description:
      "Translate English to Georgian (ქართული) online — free, instant and precise. Get natural Georgian translations for messages, essays and documents.",
    intro:
      "Convert English text into fluent Georgian instantly. Ideal for travellers, students and anyone communicating in Georgia.",
  },
  {
    slug: "translate-english-to-albanian",
    source: "english",
    target: "albanian",
    keyword: "Translate English to Albanian",
    title: "Translate English to Albanian | Free, Fast & Accurate",
    description:
      "Translate English to Albanian (Shqip) online — free, instant and accurate. Get fluent Albanian translations for messages, documents and everyday use.",
    intro:
      "Convert English text into natural Albanian instantly. Perfect for travellers, students and the Albanian diaspora worldwide.",
  },
];

export const PAIR_BY_SLUG = Object.fromEntries(PAIRS.map((p) => [p.slug, p]));

// Rich descriptions used in the "About this language" section on each translation page.
export const LANGUAGE_INFO = {
  english: {
    speakers: "1.5 billion+ worldwide",
    family: "Indo-European (Germanic)",
    region: "Global lingua franca; India's associate official language",
    paragraphs: [
      "English is a West Germanic language that has become the world's most widely used language of business, science, aviation and the internet. In India, it holds the status of an associate official language and is a link language across states.",
      "The English used online tends to be direct, sentence-first (subject-verb-object) and uses the Latin script. Translating from or to English requires careful attention to idioms, phrasal verbs and cultural context — something our AI handles gracefully.",
    ],
  },
  hindi: {
    speakers: "600 million+ worldwide",
    family: "Indo-Aryan (Indo-European)",
    region: "Northern & Central India; official language of the Union",
    paragraphs: [
      "Hindi (हिन्दी) is the most widely spoken language in India and one of the two official languages of the Indian Union. Written in the Devanagari script, it is closely related to Urdu and shares vocabulary with Sanskrit, Persian and Arabic.",
      "Hindi grammar is subject-object-verb, uses gendered nouns and honorifics (आप vs तुम vs तू) — small choices that dramatically change tone. Our translator preserves this register so your text sounds respectful and natural.",
    ],
  },
  tamil: {
    speakers: "80 million+ worldwide",
    family: "Dravidian",
    region: "Tamil Nadu, Puducherry, Sri Lanka, Singapore, Malaysia",
    paragraphs: [
      "Tamil (தமிழ்) is one of the world's oldest classical languages with a continuous literary tradition of over 2,000 years. It uses the Tamil script — an abugida with distinct rounded letterforms.",
      "Tamil is agglutinative: suffixes stack onto root words to convey tense, case and politeness. This means a single English sentence can become one long Tamil word — a reason word-for-word tools fail and why context-aware AI translation is essential.",
    ],
  },
  marathi: {
    speakers: "100 million+ worldwide",
    family: "Indo-Aryan (Indo-European)",
    region: "Maharashtra, Goa and diaspora across India",
    paragraphs: [
      "Marathi (मराठी) is the official language of Maharashtra and one of India's 22 scheduled languages. Like Hindi it uses the Devanagari script but has its own distinct vocabulary, gender rules and pluralisation patterns.",
      "Marathi speakers appreciate correct honorifics (आपण vs तू) and regional idioms that vary between Mumbai, Pune and Vidarbha. Our AI translator has been tuned to preserve these subtle differences.",
    ],
  },
  malayalam: {
    speakers: "40 million+ worldwide",
    family: "Dravidian",
    region: "Kerala, Lakshadweep and Gulf diaspora",
    paragraphs: [
      "Malayalam (മലയാളം) is the official language of Kerala and one of the six classical languages of India. It uses its own Malayalam script and is known for its rich vowel system.",
      "Malayalam is highly agglutinative and gendered, with a large Sanskrit and Portuguese-influenced vocabulary. High-quality translation requires understanding cultural cues from Kerala — festivals, food, kinship terms — which our AI captures.",
    ],
  },
  telugu: {
    speakers: "95 million+ worldwide",
    family: "Dravidian",
    region: "Andhra Pradesh, Telangana and diaspora",
    paragraphs: [
      "Telugu (తెలుగు) is famously called the 'Italian of the East' because nearly every word ends in a vowel, giving it a musical rhythm. It is the official language of Andhra Pradesh and Telangana.",
      "Written in the Telugu script, the language uses honorifics extensively and distinguishes between formal (గారు) and casual speech. Our translator adapts register based on the input so your message reads appropriately.",
    ],
  },
  gujarati: {
    speakers: "60 million+ worldwide",
    family: "Indo-Aryan (Indo-European)",
    region: "Gujarat, Daman & Diu; large diaspora in UK, US, Africa",
    paragraphs: [
      "Gujarati (ગુજરાતી) is the official language of Gujarat and is spoken by one of the largest Indian diasporas globally. It is written in the Gujarati script — a headless variant of Devanagari.",
      "Gujarati sentences follow subject-object-verb order and use post-positions. It is closely related to Hindi but has its own distinct vocabulary, especially for family, food and business terminology.",
    ],
  },
  spanish: {
    speakers: "500 million+ worldwide",
    family: "Romance (Indo-European)",
    region: "Spain, Latin America and the second-most spoken language in the US",
    paragraphs: [
      "Spanish (Español) is the world's second-most spoken native language and the official language of 20 countries. It uses the Latin alphabet with a few extra letters like ñ and accented vowels.",
      "Spanish grammar features gendered nouns, formal (usted) vs informal (tú) pronouns and rich verb conjugations. Our translator picks the right register based on context so your message reads authentically to native speakers.",
    ],
  },
  french: {
    speakers: "300 million+ worldwide",
    family: "Romance (Indo-European)",
    region: "France, Belgium, Switzerland, Canada, and many African nations",
    paragraphs: [
      "French (Français) is one of the world's most influential languages of diplomacy, cuisine, art and fashion. It uses the Latin alphabet with diacritics like é, è, à, ç.",
      "French grammar has gendered nouns, complex verb tenses and important formal (vous) vs informal (tu) distinctions. High-quality translation must respect these subtleties — something our AI handles carefully.",
    ],
  },
  mandarin: {
    speakers: "1.1 billion+ worldwide",
    family: "Sino-Tibetan",
    region: "Mainland China, Taiwan, Singapore and diaspora worldwide",
    paragraphs: [
      "Mandarin Chinese (中文) is the most spoken language in the world by native speakers. It is a tonal language written in Chinese characters (Simplified used in mainland China, Traditional in Taiwan and Hong Kong).",
      "Mandarin grammar is famously topic-prominent and has no verb conjugations for tense, gender or person — context does the work. Because Chinese is so different from English structurally, word-for-word translation fails; our translator produces natural, native-sounding output in Simplified script.",
    ],
  },
  german: {
    speakers: "130 million+ worldwide",
    family: "West Germanic (Indo-European)",
    region: "Germany, Austria, Switzerland, Liechtenstein and Belgium",
    paragraphs: [
      "German (Deutsch) is the most spoken native language in the European Union and the official language of Germany, Austria and parts of Switzerland. It uses the Latin script with the additional letters ä, ö, ü and ß.",
      "German is a highly structured language with three grammatical genders, four cases and long compound nouns. It also distinguishes between formal (Sie) and informal (du). Our translator preserves case agreement and register so your German reads correctly for the intended audience.",
    ],
  },
  mizo: {
    speakers: "1 million+",
    family: "Tibeto-Burman (Sino-Tibetan)",
    region: "Mizoram, Northeast India; small diaspora in Myanmar and Bangladesh",
    paragraphs: [
      "Mizo (Mizo ṭawng) is the official language of Mizoram, a state in Northeast India. It belongs to the Kuki-Chin branch of the Tibeto-Burman language family and is written in the Latin script, introduced by Christian missionaries in the 19th century.",
      "Mizo grammar follows a subject-object-verb word order. The language uses tonal distinctions and has a rich oral tradition of poetry and folk songs. Because Mizo uses Latin script, it is one of the few Indian languages directly readable by English speakers, though the grammar and vocabulary are entirely different.",
    ],
  },
  urdu: {
    speakers: "230 million+ worldwide",
    family: "Indo-Aryan (Indo-European)",
    region: "Pakistan (national language), India (official in several states), diaspora worldwide",
    paragraphs: [
      "Urdu (اردو) is the national language of Pakistan and one of the 22 scheduled languages of India. Written in the Nastaliq style of the Arabic script, it shares much of its grammar and basic vocabulary with Hindi but draws heavily from Persian, Arabic and Turkish for literary and formal registers.",
      "Urdu is known for its poetic tradition — ghazals, nazms and qawwalis. It uses honorifics extensively and distinguishes between formal (آپ) and informal (تم/تو) address. Our translator preserves these register differences for natural, culturally appropriate output.",
    ],
  },
  tagalog: {
    speakers: "28 million+ native, 80 million+ total",
    family: "Austronesian (Malayo-Polynesian)",
    region: "Philippines, especially Metro Manila and nearby provinces",
    paragraphs: [
      "Tagalog is an Austronesian language spoken as a first language by roughly a quarter of the Philippine population, primarily in the National Capital Region and surrounding provinces. It forms the basis of Filipino, the national language.",
      "Tagalog uses a verb-first word order and an intricate focus/voice system that marks which noun in the sentence is the topic. It borrows heavily from Spanish, English and Chinese, making it a fascinating blend of influences.",
    ],
  },
  afrikaans: {
    speakers: "7 million+ native, 20 million+ total",
    family: "West Germanic (Indo-European)",
    region: "South Africa, Namibia and diaspora",
    paragraphs: [
      "Afrikaans is a West Germanic language that developed from Dutch settlers' dialects in Southern Africa. It is one of the 11 official languages of South Africa and is widely spoken in Namibia as well.",
      "Afrikaans has a simplified grammar compared to Dutch — no verb conjugation for person, no grammatical gender for articles, and a double negative construction. Despite these simplifications, it has a rich literary tradition and is perfectly expressive for all modern needs.",
    ],
  },
  filipino: {
    speakers: "45 million+ native, 90 million+ total",
    family: "Austronesian (Malayo-Polynesian)",
    region: "Philippines (national language)",
    paragraphs: [
      "Filipino is the national language of the Philippines, standardised primarily from Tagalog with additions from other Philippine languages, English and Spanish. It is used in education, media and government across the archipelago.",
      "Filipino and Tagalog are largely mutually intelligible, but Filipino incorporates more loanwords and is considered the inclusive, national standard. It uses the Latin alphabet and follows similar grammatical patterns to Tagalog, including the verb-initial focus system.",
    ],
  },
  zulu: {
    speakers: "12 million+ native speakers",
    family: "Bantu (Niger-Congo)",
    region: "South Africa (KwaZulu-Natal), Lesotho, Eswatini, Mozambique",
    paragraphs: [
      "Zulu (isiZulu) is the most widely spoken home language in South Africa with over 12 million native speakers. It is one of the country's 11 official languages and belongs to the Nguni branch of the Bantu family.",
      "Zulu is known for its click consonants, noun class system and agglutinative structure. Verbs carry extensive prefixes and suffixes that encode tense, aspect, mood and agreement. Our translator handles these complex morphological patterns to produce natural output.",
    ],
  },
  maori: {
    speakers: "150,000+ speakers",
    family: "Austronesian (Polynesian)",
    region: "New Zealand (Aotearoa)",
    paragraphs: [
      "Māori (Te Reo Māori) is the indigenous language of New Zealand and one of the country's three official languages. It belongs to the Eastern Polynesian group and shares ancestry with Hawaiian, Tahitian and Samoan.",
      "Māori has a relatively simple phonology with long and short vowels, but its grammar uses particles and word order rather than inflection. The language is experiencing a revitalisation movement, making quality translation tools increasingly important.",
    ],
  },
  korean: {
    speakers: "80 million+ worldwide",
    family: "Koreanic (language isolate)",
    region: "South Korea, North Korea and diaspora worldwide",
    paragraphs: [
      "Korean (한국어) is spoken by over 80 million people and written in Hangul, a featural alphabet designed in the 15th century. It is the official language of both South Korea and North Korea.",
      "Korean grammar is agglutinative with subject-object-verb word order, and it has an elaborate honorific system with multiple speech levels. Choosing the right formality level is essential for natural translation — something our AI handles based on context.",
    ],
  },
  arabic: {
    speakers: "400 million+ worldwide",
    family: "Semitic (Afro-Asiatic)",
    region: "Middle East, North Africa and diaspora globally",
    paragraphs: [
      "Arabic (العربية) is one of the world's most spoken languages and the liturgical language of Islam. It is written right-to-left in the Arabic script, with Modern Standard Arabic (MSA) serving as the shared formal register across all Arabic-speaking countries.",
      "Arabic has a root-based morphology where most words derive from three-consonant roots. It features grammatical gender, case endings in formal registers, and complex verb forms. Our translator produces natural MSA output suitable for formal and everyday contexts.",
    ],
  },
  georgian: {
    speakers: "4 million+ speakers",
    family: "Kartvelian (South Caucasian)",
    region: "Georgia",
    paragraphs: [
      "Georgian (ქართული) is the official language of Georgia and the most widely spoken of the Kartvelian languages. It uses the unique Georgian script (Mkhedruli), one of the world's 14 existing alphabets.",
      "Georgian grammar is noted for its complex verb morphology, split ergativity, and lack of grammatical gender. Verbs can carry multiple prefixes and suffixes encoding subject, object, tense and version. Despite its complexity, Georgian has a rich literary tradition dating back to the 5th century.",
    ],
  },
  albanian: {
    speakers: "7.5 million+ worldwide",
    family: "Indo-European (Albanian branch)",
    region: "Albania, Kosovo, North Macedonia and diaspora in Italy, Greece and worldwide",
    paragraphs: [
      "Albanian (Shqip) is an Indo-European language that forms its own independent branch, with no close relatives. It is the official language of Albania and Kosovo, and co-official in parts of North Macedonia.",
      "Albanian has two main dialects — Gheg (north) and Tosk (south) — with the standard language based primarily on Tosk. It features a rich case system, definite articles that are suffixed to nouns, and an admirative mood unique among European languages.",
    ],
  },
};

// Sample phrases used in the SEO content tables on each translation page.
// Kept lightweight so pages remain unique yet crawlable.
export const PHRASE_LIBRARY = {
  english: [
    "Hello, how are you?",
    "Thank you very much",
    "What is your name?",
    "I love you",
    "Where is the railway station?",
    "Good morning",
    "Have a great day",
    "See you tomorrow",
  ],
  hindi: [
    "नमस्ते, आप कैसे हैं?",
    "बहुत बहुत धन्यवाद",
    "आपका नाम क्या है?",
    "मैं तुमसे प्यार करता हूँ",
    "रेलवे स्टेशन कहाँ है?",
    "सुप्रभात",
    "आपका दिन शुभ हो",
    "कल मिलते हैं",
  ],
  tamil: [
    "வணக்கம், எப்படி இருக்கிறீர்கள்?",
    "மிக்க நன்றி",
    "உங்கள் பெயர் என்ன?",
    "நான் உன்னை நேசிக்கிறேன்",
    "ரயில் நிலையம் எங்கே?",
    "காலை வணக்கம்",
    "நல்ல நாள்",
    "நாளை சந்திப்போம்",
  ],
  marathi: [
    "नमस्कार, तुम्ही कसे आहात?",
    "खूप खूप धन्यवाद",
    "तुमचं नाव काय आहे?",
    "मी तुझ्यावर प्रेम करतो",
    "रेल्वे स्थानक कुठे आहे?",
    "सुप्रभात",
    "तुमचा दिवस चांगला जावो",
    "उद्या भेटू",
  ],
  malayalam: [
    "നമസ്കാരം, സുഖമാണോ?",
    "വളരെ നന്ദി",
    "നിന്റെ പേരെന്താണ്?",
    "ഞാൻ നിന്നെ സ്നേഹിക്കുന്നു",
    "റെയിൽവേ സ്റ്റേഷൻ എവിടെയാണ്?",
    "ശുഭ പ്രഭാതം",
    "നല്ല ദിവസം",
    "നാളെ കാണാം",
  ],
  telugu: [
    "నమస్తే, మీరు ఎలా ఉన్నారు?",
    "చాలా ధన్యవాదాలు",
    "మీ పేరు ఏమిటి?",
    "నేను నిన్ను ప్రేమిస్తున్నాను",
    "రైల్వే స్టేషన్ ఎక్కడ ఉంది?",
    "శుభోదయం",
    "మంచి రోజు కావాలి",
    "రేపు కలుద్దాం",
  ],
  gujarati: [
    "નમસ્તે, તમે કેમ છો?",
    "ખૂબ ખૂબ આભાર",
    "તમારું નામ શું છે?",
    "હું તને પ્રેમ કરું છું",
    "રેલવે સ્ટેશન ક્યાં છે?",
    "શુભ પ્રભાત",
    "તમારો દિવસ સારો રહે",
    "કાલે મળીએ",
  ],
  spanish: [
    "Hola, ¿cómo estás?",
    "Muchas gracias",
    "¿Cómo te llamas?",
    "Te quiero",
    "¿Dónde está la estación de tren?",
    "Buenos días",
    "Que tengas un buen día",
    "Hasta mañana",
  ],
  french: [
    "Bonjour, comment ça va ?",
    "Merci beaucoup",
    "Comment tu t'appelles ?",
    "Je t'aime",
    "Où se trouve la gare ?",
    "Bonjour",
    "Passe une bonne journée",
    "À demain",
  ],
  mandarin: [
    "你好,你怎么样?",
    "非常感谢",
    "你叫什么名字?",
    "我爱你",
    "火车站在哪里?",
    "早上好",
    "祝你有美好的一天",
    "明天见",
  ],
  german: [
    "Hallo, wie geht es dir?",
    "Vielen Dank",
    "Wie heißt du?",
    "Ich liebe dich",
    "Wo ist der Bahnhof?",
    "Guten Morgen",
    "Einen schönen Tag noch",
    "Bis morgen",
  ],
  mizo: [
    "Chibai, i dam em?",
    "Ka lawm e, ka lawm lutuk",
    "I hming eng nge ni?",
    "Ka hmangaih che",
    "Railstation khawi ah nge ni?",
    "Zing leh mual",
    "Ni ṭha tak nei rawh",
    "Tûkah kan inhmu ang",
  ],
  urdu: [
    "ہیلو، آپ کیسے ہیں؟",
    "بہت بہت شکریہ",
    "آپ کا نام کیا ہے؟",
    "میں تم سے محبت کرتا ہوں",
    "ریلوے اسٹیشن کہاں ہے؟",
    "صبح بخیر",
    "آپ کا دن اچھا گزرے",
    "کل ملتے ہیں",
  ],
  tagalog: [
    "Kumusta ka?",
    "Maraming salamat",
    "Ano ang pangalan mo?",
    "Mahal kita",
    "Nasaan ang istasyon ng tren?",
    "Magandang umaga",
    "Magandang araw sa iyo",
    "Kita tayo bukas",
  ],
  afrikaans: [
    "Hallo, hoe gaan dit?",
    "Baie dankie",
    "Wat is jou naam?",
    "Ek is lief vir jou",
    "Waar is die treinstasie?",
    "Goeie môre",
    "Lekker dag verder",
    "Sien jou môre",
  ],
  filipino: [
    "Kumusta ka?",
    "Maraming salamat po",
    "Ano po ang pangalan ninyo?",
    "Mahal kita",
    "Nasaan po ang istasyon ng tren?",
    "Magandang umaga po",
    "Magandang araw po sa inyo",
    "Kita po tayo bukas",
  ],
  zulu: [
    "Sawubona, unjani?",
    "Ngiyabonga kakhulu",
    "Ubani igama lakho?",
    "Ngiyakuthanda",
    "Siphi isiteshi sesitimela?",
    "Sawubona ekuseni",
    "Ube nosuku oluhle",
    "Sizobonana kusasa",
  ],
  maori: [
    "Kia ora, kei te pēhea koe?",
    "Tēnā koe, kia ora rawa atu",
    "Ko wai tō ingoa?",
    "Aroha nui ki a koe",
    "Kei hea te tūranga tereina?",
    "Mōrena",
    "Kia pai tō rā",
    "Ka kite āpōpō",
  ],
  korean: [
    "안녕하세요, 어떻게 지내세요?",
    "정말 감사합니다",
    "이름이 뭐예요?",
    "사랑해요",
    "기차역이 어디에 있나요?",
    "좋은 아침이에요",
    "좋은 하루 보내세요",
    "내일 봐요",
  ],
  arabic: [
    "مرحبًا، كيف حالك؟",
    "شكرًا جزيلاً",
    "ما اسمك؟",
    "أحبك",
    "أين محطة القطار؟",
    "صباح الخير",
    "أتمنى لك يومًا سعيدًا",
    "أراك غدًا",
  ],
  georgian: [
    "გამარჯობა, როგორ ხარ?",
    "დიდი მადლობა",
    "რა გქვია?",
    "მიყვარხარ",
    "სად არის რკინიგზის სადგური?",
    "დილა მშვიდობისა",
    "კარგი დღე გისურვებ",
    "ხვალ შევხვდებით",
  ],
  albanian: [
    "Përshëndetje, si jeni?",
    "Faleminderit shumë",
    "Si e keni emrin?",
    "Të dua",
    "Ku është stacioni i trenit?",
    "Mirëmëngjes",
    "Ditë të mbarë",
    "Shihemi nesër",
  ],
};
